import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group relative block rounded-lg overflow-hidden aspect-[3/4] cursor-pointer"
        data-product-shop=""
      >
        <img
          src={comic.coverUrl}
          alt={comic.title}
          title={`${comic.title} - 免费阅读韩漫全集`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500/80 text-background-50 whitespace-nowrap">
              {comic.category}
            </span>
            {comic.status === '连载中' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500/80 text-background-50 whitespace-nowrap">
                {comic.status}
              </span>
            )}
          </div>
          <h3 className="text-lg font-heading font-bold text-background-50 mb-1">
            {comic.title}
          </h3>
          <p className="text-xs text-foreground-200 line-clamp-1">{comic.author}</p>
        </div>
        <div className="absolute top-3 right-3 bg-background-50/20 backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1">
          <i className="ri-star-fill text-yellow-400 text-xs"></i>
          <span className="text-xs font-semibold text-background-50">{comic.rating}</span>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
        data-product-shop=""
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.coverUrl}
            alt={comic.title}
            title={comic.title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 truncate">{comic.title}</h4>
          <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-primary-500">{comic.category}</span>
            <span className="text-xs text-foreground-400">{comic.views}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
      data-product-shop=""
    >
      <div className="relative overflow-hidden aspect-[3/4]">
        <img
          src={comic.coverUrl}
          alt={comic.title}
          title={`${comic.title} - 免费阅读韩漫全集`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 bg-background-50/20 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-0.5">
          <i className="ri-star-fill text-yellow-400 text-xs"></i>
          <span className="text-xs font-semibold text-background-50">{comic.rating}</span>
        </div>
        {comic.status === '已完结' && (
          <div className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
            已完结
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-900 truncate">{comic.title}</h3>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs text-foreground-500">{comic.author}</span>
          <span className="text-xs text-foreground-400">·</span>
          <span className="text-xs text-foreground-400">{comic.views}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {comic.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}